import React, { useState, useEffect, useMemo } from "react";

import { Select, MenuItem, Typography, Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";
import { getSocket } from "../../services/socket-io";

const META = new Set(["meta", "meta_coexistence"]);
const WINDOW_MS = 24 * 60 * 60 * 1000;

const useStyles = makeStyles((theme) => ({
  bar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "3px 12px",
    fontSize: 12,
    background: theme.palette.background.paper,
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    flexShrink: 0,
    flexWrap: "wrap",
  },
  select: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  staticConn: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  windowOpen: {
    fontSize: 11,
    fontWeight: 600,
    color: "#16a34a",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  windowClosed: {
    fontSize: 11,
    fontWeight: 700,
    color: "#dc2626",
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    display: "inline-block",
  },
}));

const fmtRemaining = (ms) => {
  const total = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}h ${m}m`;
};

const ChatConnectionBar = ({ ticket, onTicketUpdate }) => {
  const classes = useStyles();
  const { whatsApps } = useWhatsApps();
  const [switching, setSwitching] = useState(false);
  // Re-render cada minuto para refrescar el contador de la ventana.
  const [, setTick] = useState(0);
  // Inbound recibido en vivo por la línea activa → reabre la ventana sin reentrar al chat.
  const [localInboundAt, setLocalInboundAt] = useState(0);

  const activeId = ticket?.whatsappId;
  const ticketDbId = ticket?.id;

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // Reset del override al cambiar de ticket o de línea de salida.
  useEffect(() => {
    setLocalInboundAt(0);
  }, [ticketDbId, activeId]);

  // Socket: mismo patrón que MessageInput — un inbound del cliente (fromMe=false)
  // POR LA LÍNEA ACTIVA reabre la ventana al instante.
  useEffect(() => {
    if (!ticketDbId) return undefined;
    // SINGLETON getSocket() + join inmediato + re-join en connect + cleanup con off.
    // NUNCA openSocket()+disconnect(): en socket.io-client v3 el socket es multiplexado
    // (compartido con MessagesList/MessageInput); un disconnect() lo tira abajo para
    // todos y no auto-reconecta → el listener no recibía y la ventana no refrescaba.
    const socket = getSocket();
    socket.emit("joinChatBox", ticketDbId);
    const handleReconnect = () => socket.emit("joinChatBox", ticketDbId);
    const handleAppMessage = (data) => {
      if (
        data.action === "create" &&
        String(data.ticket?.id) === String(ticketDbId) &&
        data.message?.fromMe === false &&
        Number(data.ticket?.whatsappId) === Number(activeId)
      ) {
        setLocalInboundAt(Date.now());
      }
    };
    socket.on("connect", handleReconnect);
    socket.on("appMessage", handleAppMessage);
    return () => {
      socket.off("connect", handleReconnect);
      socket.off("appMessage", handleAppMessage);
    };
  }, [ticketDbId, activeId]);

  const active = useMemo(
    () => whatsApps.find((w) => w.id === activeId) || ticket?.whatsapp || null,
    [whatsApps, activeId, ticket]
  );

  if (!ticket?.id || !activeId) return null;

  const isMeta = META.has(active?.provider);
  // Ventana 24hs ESTRICTA: abierta SOLO si el cliente escribió a ESTA misma línea
  // (lastCustomerMessageWhatsappId === activeId) en las últimas 24hs. Si el último
  // inbound fue por otra línea (ej. QR) o no hubo inbound → cerrada.
  const sameLine =
    ticket?.lastCustomerMessageWhatsappId != null &&
    Number(ticket.lastCustomerMessageWhatsappId) === Number(activeId);
  const baseFromTicket =
    sameLine && ticket?.lastCustomerMessageAt
      ? new Date(ticket.lastCustomerMessageAt).getTime()
      : 0;
  // localInboundAt solo se setea para inbounds en la línea activa (ver socket arriba),
  // así que reabre la ventana en vivo sin reentrar al chat.
  const last = isMeta ? Math.max(baseFromTicket, localInboundAt) : 0;
  const remaining = last + WINDOW_MS - Date.now();
  const windowOpen = last > 0 && remaining > 0;

  const handleChange = async (e) => {
    const newId = e.target.value;
    if (newId === activeId) return;
    setSwitching(true);
    try {
      const { data } = await api.put(`/tickets/${ticket.id}/connection`, {
        whatsappId: newId,
      });
      onTicketUpdate?.(data);
    } catch (err) {
      toastError(err);
    }
    setSwitching(false);
  };

  const multi = whatsApps.length > 1;

  return (
    <Box className={classes.bar}>
      <WhatsAppIcon style={{ fontSize: 16, color: "#25d366" }} />
      {multi ? (
        <Select
          value={activeId}
          onChange={handleChange}
          disabled={switching}
          disableUnderline
          className={classes.select}
        >
          {whatsApps.map((w) => (
            <MenuItem key={w.id} value={w.id}>
              {w.name}
              {w.number ? ` · ${w.number}` : ""}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Typography className={classes.staticConn}>
          {active?.name}
          {active?.number ? ` · ${active.number}` : ""}
        </Typography>
      )}

      {isMeta &&
        (windowOpen ? (
          <span className={classes.windowOpen}>
            <span className={classes.dot} style={{ background: "#16a34a" }} />
            {i18n.t("chatConnectionBar.windowOpen")} ·{" "}
            {i18n.t("chatConnectionBar.closesIn", { time: fmtRemaining(remaining) })}
          </span>
        ) : (
          <span className={classes.windowClosed}>
            {i18n.t("chatConnectionBar.windowClosed")}
          </span>
        ))}
    </Box>
  );
};

export default ChatConnectionBar;
